/**
 * DisableAgentDialog — confirm soft-disable agent (9.M.7).
 *
 * Backend : PATCH /v1/agent-identities/:id { disabled: true } → `disabled_at = NOW()`.
 *
 * UX :
 *   - Confirmation explicite avant action (anti-mis-click).
 *   - Warning forensic preservation : les rows historiques *_by_agent
 *     conservent leur display_name (jointure DB toujours active).
 *   - Réversible via Enable.
 */

import { useEffect, useId, useRef } from "react";
import { useTranslation } from "react-i18next";

import { ApiException } from "../../../../lib/api";
import {
  getApiErrorMessage,
  normalizeLang,
} from "../../../../lib/apiErrorMessage";
import { useUpdateAgentDisabled } from "../../../../lib/queries/agentIdentities";
import { toast } from "../../../../lib/toast";

type Props = {
  readonly agentId: string;
  readonly agentDisplayName: string;
  readonly onClose: () => void;
};

export function DisableAgentDialog({ agentId, agentDisplayName, onClose }: Props) {
  const { t, i18n } = useTranslation();
  const lang = normalizeLang(i18n.resolvedLanguage);
  const titleId = useId();
  const cancelBtnRef = useRef<HTMLButtonElement | null>(null);

  const update = useUpdateAgentDisabled(agentId);
  const { reset: resetMutation } = update;

  useEffect(() => {
    resetMutation();
    queueMicrotask(() => cancelBtnRef.current?.focus());
  }, [resetMutation]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !update.isPending) {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose, update.isPending]);

  const onConfirm = () => {
    update.mutate(true, {
      onSuccess: () => {
        toast.success(t("agent_identities.disable_dialog.success"));
        onClose();
      },
    });
  };

  const apiErrorMsg =
    update.error instanceof ApiException
      ? getApiErrorMessage(update.error, lang) || t("errors.generic")
      : update.isError
        ? t("errors.generic")
        : null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
    >
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
        onClick={() => {
          if (!update.isPending) onClose();
        }}
      />
      <div className="relative z-10 w-full max-w-md rounded-xl bg-white shadow-xl ring-1 ring-slate-200 motion-safe:animate-fade-in">
        <header className="border-b border-slate-100 px-5 py-4">
          <h2 id={titleId} className="text-base font-semibold text-slate-900">
            {t("agent_identities.disable_dialog.title")}
          </h2>
        </header>

        <div className="space-y-4 px-5 py-5">
          <p className="text-sm text-slate-700">
            {t("agent_identities.disable_dialog.body", { name: agentDisplayName })}
          </p>
          <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            {t("agent_identities.disable_dialog.forensic_note")}
          </p>

          {apiErrorMsg && (
            <div
              role="alert"
              className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
            >
              {apiErrorMsg}
            </div>
          )}
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-slate-100 px-5 py-4">
          <button
            ref={cancelBtnRef}
            type="button"
            onClick={onClose}
            disabled={update.isPending}
            className="rounded-md px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 disabled:opacity-50 touch-manipulation"
          >
            {t("common.cancel")}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={update.isPending}
            aria-busy={update.isPending}
            className="inline-flex items-center gap-2 rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-300 touch-manipulation"
          >
            {update.isPending
              ? t("agent_identities.disable_dialog.submitting")
              : t("agent_identities.disable_dialog.submit")}
          </button>
        </footer>
      </div>
    </div>
  );
}
